import { useState } from 'react'
import { Controller, useFormContext } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { MileageRateConfigurationFragment } from 'generated/resources/graphql'

import NumberField from 'modules/core/components/NumberField'
import SelectField from 'modules/core/components/SelectField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import i18n from 'modules/core/lib/i18n'

import { castError } from 'modules/expenses/components/expenses/Detail/_newForm/castError'
import withCustomizationFields, {
  WithCustomizationFieldsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withCustomizationFields'
import withFieldPermissions, {
  WithFieldPermissionsProps,
} from 'modules/expenses/components/expenses/Detail/_newForm/withFieldPermissions'
import { getLabelFromRate } from 'modules/expenses/lib/expensables/expensable'

const FIELD_NAME = 'rate'

interface Props extends WithFieldPermissionsProps, WithCustomizationFieldsProps {
  rates?: MileageRateConfigurationFragment[]
}

const RateSelectorInput = ({ rates, isFieldReadOnly, isFieldMandatory }: Props) => {
  const {
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext()

  const currency = watch('currency')
  // NOTE: This component is intentionally subscribed to its own value using watch().
  // The underlying input component from the deprecated design system does not reliably
  // re-render when its `value` prop changes, causing a "stale UI" bug.
  // Calling watch() creates a subscription that forces this wrapper component to
  // re-render on every change, ensuring the UI stays in sync with the form state.
  // This is a targeted workaround for the external library's rendering issue.
  const rate = watch('rate')

  const {
    access: { locale },
  } = useCurrentSession()

  const initialRate = rates?.find((r) => r.rate === rate)
  const [selectedRate, setSelectedRate] = useState<MileageRateConfigurationFragment | null>(
    initialRate ?? null
  )

  const rateForSelect = rates?.map((rateItem) => ({
    label: getLabelFromRate(locale, currency, Number(rateItem.rate), rateItem.name),
    value: rateItem.id,
  }))

  const getRate = (rateId: string) => {
    return rates?.find((mileageRate) => mileageRate.id === rateId)
  }

  return (
    <Box width="full">
      <Controller
        name={FIELD_NAME}
        control={control}
        render={() => (
          <SelectField
            label={i18n.t('expenses_v2.forms.fields.rate.label')}
            none={i18n.t('expenses_v2.forms.new_form.fields.rate.placeholder')}
            options={rateForSelect ?? []}
            value={selectedRate?.id?.toString() ?? ''}
            onChange={(value) => {
              // Given the structure of the rates options we need to set the rate data programatically
              const rateValue = getRate(String(value))
              setSelectedRate(rateValue ?? null)
              setValue('rate', rateValue?.rate, {
                shouldValidate: true,
                shouldDirty: true,
              })
            }}
            error={castError(errors?.rate)}
            readonly={isFieldReadOnly}
            mandatory={isFieldMandatory}
          />
        )}
      />
    </Box>
  )
}

const RateInput = ({ isFieldMandatory, isFieldReadOnly }: Props) => {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext()

  watch('rate')
  return (
    <Box width="full">
      <Controller
        name={FIELD_NAME}
        control={control}
        render={({ field }) => (
          <NumberField
            label={i18n.t('expenses_v2.forms.fields.rate.label')}
            placeholder="0.0"
            value={field.value}
            maxDecimals={5}
            mandatory={isFieldMandatory ?? false}
            readonly={isFieldReadOnly}
            onChange={(value) => {
              field.onChange(value)
            }}
            error={castError(errors?.rate)}
          />
        )}
      />
    </Box>
  )
}

const RateField = (props: Props) => {
  const { rates, isFieldReadOnly, isFieldMandatory } = props

  if (rates?.length === 0) {
    return <RateInput {...props} />
  } else {
    return (
      <RateSelectorInput
        {...props}
        rates={rates || []}
        isFieldReadOnly={isFieldReadOnly}
        isFieldMandatory={isFieldMandatory}
      />
    )
  }
}

export default withCustomizationFields(withFieldPermissions(RateField, 'rate'))
